/**
 * Header fixe (sticky) : titre, date de mise à jour, filtres globaux,
 * bouton Actualiser et menu utilisateur connecté.
 */
import { useState } from "react";
import { ChevronDown, LogOut, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useFilters } from "@/contexts/FilterContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import GlobalFilters from "@/components/filters/GlobalFilters";
import { toast } from "@/hooks/use-toast";
import { HEADER } from "@/constants/testIds";

export default function Header() {
  const { user, logout } = useAuth();
  const { refresh } = useFilters();
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    refresh();
    toast({ title: "Dashboard actualisé", description: "Toutes les données ont été rechargées." });
    setTimeout(() => setRefreshing(false), 600);
  };

  return (
    <header data-testid={HEADER.container} className="sticky top-0 z-20 border-b border-slate-200 bg-white">
      <div className="flex h-16 items-center justify-between gap-4 px-4 md:px-6">
        <div className="flex items-center gap-3">
          <img src="/lafarge-mark.png" alt="Lafarge" className="hidden h-7 w-auto object-contain sm:block" />
          <div>
            <h1 className="text-lg font-bold tracking-tight text-slate-800">Dashboard Sécurité Direction</h1>
            <p className="text-xs text-slate-500">
              Mise à jour le {new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            data-testid={HEADER.refreshButton}
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="gap-2 border-slate-200"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={HEADER.userMenuTrigger}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors duration-200 hover:bg-slate-100"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#0B5394] text-xs text-white">
                    {(user?.name || "U").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium leading-none text-slate-700">{user?.name}</p>
                  <p className="text-xs text-slate-400">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={HEADER.logoutButton} onClick={logout} className="gap-2 text-red-600">
                <LogOut className="h-4 w-4" /> Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <GlobalFilters />
    </header>
  );
}
